import { Fragment, useEffect, useState } from 'react';

import { useMutation } from '@apollo/client';
import { useAmplitude } from '@crehana/analytics';
// biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
import { Dropdown, PrimaryButton, TextField } from '@crehana/ui';
import { PhoneInput } from '@crehana/web/PhoneInput';
import { useMe } from '@crehana/web/ReactContexts/AuthContext';
// biome-ignore lint/style/noRestrictedImports: legacy code - style/noRestrictedImports
import { Controller, useForm } from 'react-hook-form';

import { REGEX_EMAIL } from '@/shared/utils/regex';
import Steps from '@/views/B2BCheckout/components/Steps';
import NameField from '@/views/B2BCheckout/components/UserDataFields/NameField';
import { ACTIONS, type CountryType } from '@/views/B2BCheckout/context/types';
import CREATE_B2B_PAYMENT_OPPORTUNITY from '@/views/B2BCheckout/graphql/CreateB2BPaymentOpportunity.v4.b2b.graphql';
import type {
  CreateB2BPaymentOpportunity,
  CreateB2BPaymentOpportunityVariables,
} from '@/views/B2BCheckout/graphql/types/CreateB2BPaymentOpportunity';
import useCheckoutCountriesOptions from '@/views/B2BCheckout/hooks/useCheckoutCountriesOptions';
import useCheckoutCtx from '@/views/B2BCheckout/hooks/useCheckoutCtx';

import type { NameState } from '../../../../components/UserDataFields/NameField/types';
import { INPUT_MAX_LENGTH, validations } from '../constants';
import type { FormData, SubmitData } from './types';

const AccountForm = () => {
  const {
    dispatch,
    state: { currentStep, accountDetails, licences },
    ONBOARDING_STEPS,
    loading: loadingPrice,
  } = useCheckoutCtx();
  const [country, setCountry] = useState<CountryType>(
    accountDetails?.country || undefined,
  );
  const { countries, loading } = useCheckoutCountriesOptions();
  const { me } = useMe();
  const { logEvent } = useAmplitude();

  const [createB2BPaymentOpportunity, { error }] = useMutation<
    CreateB2BPaymentOpportunity,
    CreateB2BPaymentOpportunityVariables
  >(CREATE_B2B_PAYMENT_OPPORTUNITY, {
    context: { clientName: 'v4.b2b' },
  });

  const [firstName, setFirstName] = useState<NameState>({
    name: accountDetails?.firstName || '',
    isValid: !!accountDetails?.firstName,
  });
  const [lastName, setLastName] = useState<NameState>({
    name: accountDetails?.lastName || '',
    isValid: !!accountDetails?.lastName,
  });

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy code - correctness/useExhaustiveDependencies
  useEffect(() => {
    logEvent({
      event: 'Render B2B Account Form',
      data: {
        licences,
      },
    });
  }, []);

  const {
    handleSubmit,
    register,
    formState: { isSubmitting, isValid },
    errors,
    control,
  } = useForm<FormData>({
    mode: 'onChange',
  });

  const onSubmit = async (formData: SubmitData) => {
    const {
      email,
      role,
      company,
      phoneNumber: { code, phoneNumber },
      docNroCompany,
    } = formData;

    try {
      const response = await createB2BPaymentOpportunity({
        variables: {
          createB2BOpportunityInput: {
            b2bOpportunity: {
              organizationEmail: email,
              companyName: company,
              limit: licences,
              firstName: firstName.name,
              lastName: lastName.name,
              userId: me?.originalId ? `${me?.originalId}` : '',
              phone: code + phoneNumber,
              countryId: country?.id,
              position: role,
              docNroCompany,
            },
          },
        },
      });

      if (response?.data?.createOpportunity?.success) {
        logEvent({
          event: 'Submit B2B Account Form Success',
          data: {},
          callback: () => {
            dispatch({
              type: ACTIONS.SET_ACCOUNT_DETAILS,
              payload: {
                firstName: firstName.name,
                lastName: lastName.name,
                email,
                role,
                company,
                phoneNumber: {
                  code,
                  phoneNumber,
                },
                country,
                opportunityId:
                  response?.data?.createOpportunity?.opportunity?.originalId,
                docNroCompany,
              },
            });
            dispatch({
              type: ACTIONS.NAVIGATE,
              payload: 3,
            });
          },
        });
      }
    } catch (err) {
      logEvent({
        event: 'Submit B2B Account Form Error',
        data: {},
        callback: () => {
          if (window.Sentry?.captureMessage) {
            window.Sentry.captureMessage(
              'B2BChackout: createB2BPaymentOpportunity mutation error',
              {
                err,
              },
            );
          }
        },
      });
    }
  };

  return (
    <Fragment>
      <Steps
        currentStep={currentStep}
        steps={ONBOARDING_STEPS}
        className="mb-32 sm:mb-56"
      />
      <form onSubmit={handleSubmit(onSubmit)} className="flex flex-wrap w-full">
        <div className="w-full lg:w-1/2 lg:pr-12">
          <NameField onChange={setFirstName} value={firstName.name} />
        </div>
        <div className="w-full lg:w-1/2 lg:pl-12">
          <NameField
            onChange={setLastName}
            value={lastName.name}
            isLastName={true}
          />
        </div>
        <div className="mb-24  w-full lg:w-1/2 lg:pr-12">
          <Controller
            control={control}
            name="phoneNumber"
            rules={{
              required: true,
              validate: data => data.phoneNumber && data.isValid,
            }}
            defaultValue={me?.phoneNumber}
            render={({ onChange }) => (
              <PhoneInput
                defaultCountry={country?.value || 'pe'}
                className="w-full mx-auto text-white"
                showAll
                onChange={onChange}
                label="Número de teléfono"
                isRequired
                initialState={{
                  code: accountDetails?.phoneNumber.code || '',
                  phoneNumber: accountDetails?.phoneNumber.phoneNumber || '',
                }}
              />
            )}
          />
        </div>
        <div className="mb-24  w-full lg:w-1/2 lg:pl-12">
          <TextField
            label="Correo corporativo"
            name="email"
            data-testid="email"
            maxLength={INPUT_MAX_LENGTH}
            ref={register({
              ...validations,
              pattern: {
                value: REGEX_EMAIL,
                message: 'Ingresa una dirección de correo válida',
              },
            })}
            errorText={errors.email?.message}
            disabled={isSubmitting}
            full
            defaultValue={accountDetails?.email}
          />
        </div>
        <div className="mb-24  w-full lg:w-1/2 lg:pr-12">
          <TextField
            label="Puesto o cargo"
            name="role"
            data-testid="role"
            maxLength={INPUT_MAX_LENGTH}
            ref={register(validations)}
            errorText={errors.role?.message}
            disabled={isSubmitting}
            full
            defaultValue={accountDetails?.role}
          />
        </div>
        <div className="mb-24  w-full lg:w-1/2 lg:pl-12">
          <TextField
            label="Empresa"
            name="company"
            data-testid="company"
            maxLength={INPUT_MAX_LENGTH}
            ref={register(validations)}
            errorText={errors.company?.message}
            disabled={isSubmitting}
            full
            defaultValue={accountDetails?.company}
          />
        </div>
        <div className="mb-24  w-full lg:w-1/2 lg:pr-12">
          <TextField
            label="Documento de Empresa"
            name="docNroCompany"
            data-testid="docNroCompany"
            maxLength={INPUT_MAX_LENGTH}
            ref={register(validations)}
            errorText={errors.docNroCompany?.message}
            disabled={isSubmitting}
            full
            defaultValue={accountDetails?.docNroCompany}
          />
        </div>
        <div className="mb-24 w-full lg:w-1/2 lg:pl-12">
          <Dropdown
            name="country"
            label="País"
            data-testid="country"
            className="w-full"
            full
            options={countries}
            selected={country}
            onSelected={selected => {
              setCountry(selected as CountryType);
            }}
            disabled={isSubmitting || loading}
            required
          />
        </div>
        {error && (
          <div className="font-subtitle4 flex justify-center md:justify-start text-red-main py-8 w-full">
            Error: {error.message}
          </div>
        )}
        <div className="flex flex-col sm:flex-row items-center w-full">
          <div className="mt-16 w-full lg:w-1/2 lg:pr-12">
            <PrimaryButton
              label="Continuar"
              full
              data-testid="submitButton"
              type="submit"
              disabled={
                !(
                  isValid &&
                  firstName.isValid &&
                  lastName.isValid &&
                  !!country
                ) ||
                isSubmitting ||
                loadingPrice
              }
            />
          </div>
        </div>
      </form>
    </Fragment>
  );
};

export default AccountForm;
